import { Component, Input, OnChanges } from '@angular/core';

import { DisplayType, OptionStatus, ParticipantPriceType } from '@cds/betting-offer';
import { EventOption, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { HorseRaceSortingDirection, V3FixtureParticipantPriceMap, WinGameFixture, WinGameParticipant } from '@frontend/sports/racing/feature/models';
import { trackingConstants } from '@frontend/sports/tracking/feature';
import { ParticipantPickType } from 'packages/sports/common/betslip/core/picks/pick-models';

import { HorsesFixtureParticipantHelper } from '../../../horse-racing-shared/services/horses-fixture-participant-helper.service';
import { HorsesFixturePricingHelper } from '../../../horse-racing-shared/services/horses-fixture-pricing-helper.service';
import { HorsesFixtureSortingHelper } from '../../../horse-racing-shared/services/horses-fixture-sorting-helper.service';
import { HorsesRaceService } from '../../services/horses-race.service';

interface HorsesRunnerViewModel {
    horse: WinGameParticipant;
    eventOption?: EventOption;
}

interface AntepostOptionMarketViewModel {
    fixture: WinGameFixture;
    participants: HorsesRunnerViewModel[];
}

@Component({
    selector: 'ms-antepost-option-market',
    templateUrl: 'antepost-option-market.component.html',
    standalone: false,
})
export class AntepostOptionMarketComponent implements OnChanges {
    private static readonly emptyViewModel = {} as AntepostOptionMarketViewModel;
    @Input() fixture: WinGameFixture;
    @Input() sortBy: HorseRaceSortingDirection;
    isGreyhounds: boolean;
    vm: AntepostOptionMarketViewModel = AntepostOptionMarketComponent.emptyViewModel;
    optionGroups: EventOptionGroup[];
    priceType = {
        Fixed: ParticipantPriceType.Fixed,
        StartingPrice: ParticipantPriceType.StartingPrice,
    };

    optionsT: EventOption[];
    pickType = ParticipantPickType.Win;
    selectedOptionGroup: EventOptionGroup;
    trackingSource: string;
    fixtureParticipants: WinGameParticipant[];

    constructor(
        private horsesFixtureSortingHelper: HorsesFixtureSortingHelper,
        private horsesFixtureParticipantHelper: HorsesFixtureParticipantHelper,
        private horsesFixturePricingHelper: HorsesFixturePricingHelper,
        private horsesRaceService: HorsesRaceService,
        public sitecore: Sitecore,
    ) {}

    ngOnChanges(changes: ISimpleChanges<AntepostOptionMarketComponent>): void {
        this.trackingSource = this.fixture.sport.id === SportConstant.Greyhounds ? 'Greyhounds_Racecard' : 'horseracing_Racecard';
        if (changes.fixture !== undefined) {
            this.fixture = changes.fixture.currentValue;
        }
        if (changes.fixture?.firstChange || changes.sortBy?.currentValue === HorseRaceSortingDirection.Odds) {
            this.sortBy = HorseRaceSortingDirection.Odds;
        }
        this.updateViewModel(this.fixture);
        if (this.sortBy === HorseRaceSortingDirection.AtoZ) {
            this.sortByAtoZ();
        }
    }

    private updateViewModel(fixture: WinGameFixture): void {
        const runners = this.horsesFixtureParticipantHelper.getParticipants(this.fixture.participants);
        this.optionGroups = fixture.optionGroups.filter((t) => t.detailedGrouping.displayType === DisplayType.Wincast);
        this.selectedOptionGroup = this.optionGroups[0];
        const options = this.horsesFixturePricingHelper.getOptionPrices(this.fixture, this.selectedOptionGroup, ParticipantPriceType.Fixed);
        let participants;
        if (this.sortBy === HorseRaceSortingDirection.Odds) {
            this.fixtureParticipants = this.horsesFixtureSortingHelper.sortV3ParticipantsBy(runners, options, this.sortBy);
        } else {
            this.fixtureParticipants = this.horsesRaceService.restoreParticipantOrder(this.fixtureParticipants, this.fixture);
        }
        participants = this.buildAntepostRunners(fixture, this.fixtureParticipants, options);
        this.vm = {
            fixture,
            participants,
        };
    }

    private buildAntepostRunners(
        fixture: WinGameFixture,
        participants: WinGameParticipant[],
        options: V3FixtureParticipantPriceMap,
    ): HorsesRunnerViewModel[] {
        return participants.map((participant) => {
            const eventOption = options[participant.id];
            if (eventOption) {
                eventOption.online =
                    eventOption.status === OptionStatus.Visible &&
                    !this.horsesFixturePricingHelper.isDerivedMarketPriceLocked(fixture, participant, eventOption);
            }

            return {
                horse: participant,
                eventOption,
            };
        });
    }

    private sortByAtoZ() {
        if (this.vm !== undefined) {
            this.vm.participants = this.vm.participants.sort((a, b) => a.horse.name.value.localeCompare(b.horse.name.value));
        }
    }

    getTrackingOptions(fixture: WinGameFixture) {
        const date = new Date(fixture.startDate);
        if (fixture.sport.id == SportConstant.Greyhounds) {
            this.isGreyhounds = true;
        }

        return {
            [trackingConstants.COMPONENT_MODULE_CUSTOM_NAME]: fixture.meeting.name.value,
            [trackingConstants.COMPONENT_MODULE_NAME]: this.isGreyhounds ? 'Greyhound_Racecard' : 'horseracing_Racecard',
            [trackingConstants.COMPONENT_MODULE_POSITION]: date.getHours() + ':' + date.getMinutes(),
            [trackingConstants.COMPONENT_MODULE_SOURCE]: '',
            [trackingConstants.COMPONENT_BET_SOURCE]: this.isGreyhounds ? 'Greyhound_Racecard/future' : 'horseracing_Racecard/future',
            [trackingConstants.BET_CALENDER]: 'future',
            [trackingConstants.RESULT_ID]: 'not applicable',
            [trackingConstants.SPORT_LEAGUE_ID]: 'not applicable',
        };
    }
}
